/**
 * Rule Element plumbing shared by the Character's derivation (./engine.ts) and the Familiar's
 * (./familiar.ts): parsing an item's Rule Elements into instances, running a phase over them, and
 * collecting the ChoiceSet prompts they pose.
 */
import type { AnswerValue } from "../character/model";
import type { EngineItem } from "./items";
import { EnginePhysical } from "./physical";
import { RuleInstance, type RuleSource } from "./rule-elements/base";
import { inflateChoices, isChoiceSetOpen, selectionLabel, validateSelection, type Candidate, type ChoiceForm, type ChoiceSetData } from "./rule-elements/choice-set";
import { HANDLERS } from "./rule-elements/registry";
import type { EngineState } from "./state";

/** A ChoiceSet on an Entry the Character (or its Familiar) holds: what it asks, what was answered, what may be chosen. */
export interface ChoicePrompt {
  /** The Choice holding the Entry; its `answers[flag]` is the answer. */
  choiceKey: string | null;
  uuid: string;
  name: string;
  index: number;
  flag: string;
  /** The prompt text (localised when the catalogue carries the string). */
  prompt: string;
  label: string;
  form: ChoiceForm | null;
  selection: AnswerValue | null;
  selectionLabel: string | null;
  /** No answer yet and one is required: the Entry's Rule Elements are inactive until it is made. */
  open: boolean;
  /** Whether the answer is among the candidates; null when that cannot be checked yet. */
  legal: boolean | null;
  allowNoSelection: boolean;
  /** Computed for open prompts, or for all when `prompts: "all"` was asked; null otherwise. */
  candidates: Candidate[] | null;
  /** False when the candidate source is not available yet (equipment, Strikes, a missing catalogue). */
  available: boolean | null;
  note: string | null;
  containsItems: boolean;
}

/** The selector or path a Rule Element names, when one can be read. */
export function readSelector(source: RuleSource): string | null {
  const selector = source.selector ?? source.selectors ?? source.path;
  if (typeof selector === "string") return selector;
  if (Array.isArray(selector)) return selector.filter((s): s is string => typeof s === "string").join(", ") || null;
  return null;
}

/** Parse every item's Rule Elements, sorted by priority within the item order. */
export function createRules(state: EngineState): RuleInstance[] {
  const rules: RuleInstance[] = [];
  for (const item of state.items) rules.push(...createRulesFor(item, state));
  return rules.map((r, i) => [r, i] as const).sort(([a, ai], [b, bi]) => a.priority - b.priority || ai - bi).map(([r]) => r);
}

/** Parse one item's Rule Elements. An unanswered ChoiceSet inactivates every sibling, as in Foundry. */
export function createRulesFor(item: EngineItem, state: EngineState): RuleInstance[] {
  const rules: RuleInstance[] = [];
  const originalName = item.name;
  const unlabelled = new Set<RuleInstance>();
  item.rules.forEach((raw, index) => {
    const key = raw.key;
    const source: RuleSource = { ...raw, key: typeof key === "string" ? key : "" };
    const handler = typeof key === "string" ? (HANDLERS[key] ?? null) : null;
    const rule = new RuleInstance(item, index, source, handler);
    // A physical Entry's Rule Elements apply only while it is equipped and, when it takes investiture, invested (Foundry: RuleElement constructor).
    if (item instanceof EnginePhysical && !rule.ignored) {
      const requiresEquipped = source.requiresEquipped !== false;
      const requiresInvestment = item.isInvested === null ? false : typeof source.requiresInvestment === "boolean" ? source.requiresInvestment : requiresEquipped;
      if ((requiresEquipped && !item.isEquipped) || item.carryType === "dropped" || (requiresInvestment && !item.isInvested)) rule.ignored = true;
    }
    if (typeof source.label !== "string" || !source.label) unlabelled.add(rule);
    else if (state.catalogue) rule.label = state.catalogue.localize(rule.label);
    if (typeof key !== "string" || !key) rule.fail("key: missing");
    else if (handler && !rule.ignored) {
      try {
        handler.parse(rule, state);
      } catch (error) {
        rule.fail(`malformed: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
    rules.push(rule);
  });
  // A rule with no label of its own follows the item's name, which a ChoiceSet's adjustName may have changed.
  if (item.name !== originalName) for (const rule of unlabelled) rule.label = item.name;
  if (rules.some(isChoiceSetOpen)) for (const rule of rules) rule.ignored = true;
  // A deactivated Condition withholds every Rule Element (Foundry: `ConditionPF2e#prepareRuleElements`).
  if (!item.active) for (const rule of rules) rule.ignored = true;
  // A BattleForm's sibling rules are the form's own (Foundry marks them at item creation).
  if (rules.some((r) => r.key === "BattleForm" && !r.ignored)) {
    for (const rule of rules) if (["DamageDice", "FlatModifier", "ItemAlteration", "Note"].includes(rule.key)) rule.data.battleForm = true;
  }
  return rules;
}

export function collectPrompts(state: EngineState, mode: "open" | "all"): ChoicePrompt[] {
  const prompts: ChoicePrompt[] = [];
  for (const rule of state.rules) {
    if (rule.key !== "ChoiceSet" || !rule.handler) continue;
    const data = rule.data as unknown as ChoiceSetData;
    if (!data.form) continue;
    const item = rule.item;
    const open = data.open;
    let candidates: Candidate[] | null = null;
    let available: boolean | null = null;
    let note: string | null = null;
    if (open || mode === "all") {
      const inflated = inflateChoices(rule, state, data, !item.preselected.has(data.flag));
      candidates = inflated.candidates;
      available = inflated.available;
      note = inflated.note;
      if (open && available && candidates.length === 0) note = "no legal candidate: the Choice stays open";
    }
    let legal: boolean | null = null;
    if (data.selection !== null) {
      const verdict = validateSelection(rule, state, data);
      legal = verdict.legal;
      if (verdict.note) note = verdict.note;
    }
    prompts.push({
      choiceKey: item.choiceKey ?? null,
      uuid: item.uuid,
      name: item.name,
      index: rule.index,
      flag: data.flag,
      prompt: state.catalogue?.localize(data.prompt) ?? data.prompt,
      label: rule.label,
      form: data.form,
      selection: data.selection,
      selectionLabel: selectionLabel(rule, state, data),
      open,
      legal,
      allowNoSelection: data.allowNoSelection,
      candidates,
      available,
      note,
      containsItems: data.form === "pack" || !!data.allowedDrops || (candidates?.some((c) => c.uuid !== null) ?? false),
    });
  }
  return prompts;
}

export function runPhase(rule: RuleInstance, state: EngineState, phase: "onApplyActiveEffects" | "beforePrepareData" | "afterPrepareData"): void {
  const fn = rule.handler?.[phase];
  if (!fn || rule.ignored) return;
  try {
    fn(rule, state);
  } catch (error) {
    rule.fail(`${phase} failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}
